/**
 * Auto-simulate helpers used by the SIMULATE button. Picks sane defaults so
 * users can see a composite payoff without manually browsing markets:
 *   - BTC perp on Hyperliquid
 *   - A non-resolved BTC-related Polymarket question whose YES/NO odds are
 *     well-inside the book (avoid markets where one side is at 99/100¢ — those
 *     are basically resolved already and the composite chart would be
 *     uninteresting).
 */

import { HyperliquidInfoClient } from "@hyperhedge/hyperliquid/client";
import { PolymarketClient } from "@hyperhedge/polymarket";
import type { SelectedPerp, SelectedPred } from "../types";

export async function fetchBtcPerp(): Promise<SelectedPerp | null> {
  const client = new HyperliquidInfoClient();
  try {
    const [meta, ctxs] = await client.getMetaAndAssetCtxs();
    const idx = meta.universe.findIndex((u) => u.name === "BTC");
    if (idx < 0) return null;
    const info = meta.universe[idx];
    const ctx = ctxs[idx];
    if (!info || !ctx) return null;
    const markPx = Number(ctx.markPx);
    const prevDayPx = Number(ctx.prevDayPx);
    return {
      coin: info.name,
      markPx,
      dayChangePct:
        prevDayPx > 0 ? ((markPx - prevDayPx) / prevDayPx) * 100 : 0,
      dayNtlVlm: Number(ctx.dayNtlVlm),
      openInterest: Number(ctx.openInterest) * markPx,
      fundingPct: Number(ctx.funding) * 100,
      maxLeverage: info.maxLeverage,
    };
  } catch {
    return null;
  }
}

function parseJsonArray(s: string | undefined): string[] {
  if (!s) return [];
  try {
    const v = JSON.parse(s) as unknown;
    return Array.isArray(v) ? v.map((x) => String(x)) : [];
  } catch {
    return [];
  }
}

/**
 * Predicate for a "tradeable" BTC-related Polymarket market.
 *
 *  - Question name mentions "Bitcoin" or "BTC"
 *  - Both YES and NO prices are between 2¢ and 98¢ (so neither side is
 *    effectively resolved — keeps the composite payoff curve interesting)
 *  - Has CLOB token ids (otherwise we can't actually trade it)
 */
function isTradeableBtcMarket(
  question: string | undefined,
  yes: number | undefined,
  no: number | undefined,
  tokens: string[],
): boolean {
  if (!question) return false;
  const q = question.toLowerCase();
  const isBtc = q.includes("bitcoin") || /(^|\W)btc(\W|$)/i.test(question);
  if (!isBtc) return false;
  if (tokens.length < 2) return false;
  if (yes === undefined || no === undefined) return false;
  if (!isFinite(yes) || !isFinite(no)) return false;
  // Stay well inside the book — anything outside [0.10, 0.90] makes the
  // composite payoff curve uninteresting (one side basically resolved).
  if (yes >= 0.9 || yes <= 0.1) return false;
  if (no >= 0.9 || no <= 0.1) return false;
  return true;
}

export async function fetchBtcPrediction(opts: {
  gammaUrl?: string;
  clobUrl?: string;
} = {}): Promise<SelectedPred | null> {
  const client = new PolymarketClient({
    gammaUrl: opts.gammaUrl,
    clobUrl: opts.clobUrl,
  });
  // Polymarket sorts by 24h volume by default, so the most-traded BTC question
  // (which is what a user expects from "auto-pick") tends to fall out first.
  // Fetch a generous slice and filter.
  const tagSlugs = ["crypto", undefined] as const;
  const seen = new Set<string>();
  for (const tagSlug of tagSlugs) {
    let markets;
    try {
      markets = await client.listMarkets({ limit: 200, tagSlug });
    } catch {
      continue;
    }
    for (const m of markets) {
      if (!m.id || seen.has(m.id)) continue;
      seen.add(m.id);

      const labels = parseJsonArray(m.outcomes);
      const prices = parseJsonArray(m.outcomePrices).map(Number);
      const tokens = parseJsonArray(m.clobTokenIds);
      const yesIdx = labels.findIndex((o) => o.toLowerCase() === "yes");
      const yes = yesIdx >= 0 ? prices[yesIdx] : prices[0];
      const no = yesIdx >= 0 ? prices[1 - yesIdx] : prices[1];

      if (!isTradeableBtcMarket(m.question, yes, no, tokens)) continue;

      const yesTokenId = yesIdx >= 0 ? tokens[yesIdx] : tokens[0];
      return {
        id: m.id,
        conditionId: m.conditionId,
        clobTokenIds: tokens,
        yesTokenId,
        question: m.question,
        image: m.image,
        yesPrice: yes,
        noPrice: no,
        volume24hr: Number(m.volume24hr ?? 0),
        liquidity: Number(m.liquidity ?? 0),
        endDate: m.endDate,
        category: m.category,
        negRisk: m.negRisk ?? false,
      };
    }
  }
  return null;
}
